import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { constraintToPhrase, type Constraint } from '@il-colpo/engine';
import { Button } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { SafeObject } from './objects/SafeObject.tsx';
import { Bystander, ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface SafeView {
  digits: number;
  repeats: boolean;
  history?: { digits: number[]; clicks: number; hums: number }[];
  attemptsLeft?: number;
  lockedUntilMs?: number;
  clues: { safeProfile?: Constraint[] };
}

const HEARD = [
  'safeDigit',
  'safeClue',
  'safeNo',
  'safeSum',
  'safeParity',
  'safeGreater',
  'safeAttempt',
] as const;

/** Safe (GDD §8.2): a combination found from the owner's profile and the safe's own answers. */
export function SafePanel({ puzzleId, type, v: raw }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { role, act, view } = useGame();
  const v = raw as unknown as SafeView;
  const [entry, setEntry] = useState<number[]>([]);

  if (role === 'locksmith') {
    const jammed = (v.lockedUntilMs ?? 0) > (view.exec?.elapsedMs ?? 0);
    const full = entry.length === v.digits;
    const answer = v.history?.at(-1);
    return (
      <div className={styles['stack']}>
        <SafeObject
          typed={entry.at(-1) ?? null}
          left={v.attemptsLeft ?? 0}
          clicks={answer?.clicks ?? 0}
          hums={answer?.hums ?? 0}
          jammed={jammed}
          turns={v.history?.length ?? 0}
        />
        <p className={styles['progress']}>
          {t('puzzle.safe.left', { count: v.attemptsLeft ?? 0 })}
          {v.repeats ? ` · ${t('puzzle.safe.repeats')}` : ` · ${t('puzzle.safe.distinct')}`}
        </p>
        <div className={styles['digits']} aria-live="polite">
          {Array.from({ length: v.digits }, (_, i) => (
            <span key={i} className={styles['digit']}>
              {entry[i] ?? '·'}
            </span>
          ))}
        </div>
        <div className={styles['keypad']} role="group" aria-label={t('puzzle.safe.keypad')}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((d) => (
            <button
              key={d}
              type="button"
              className={styles['key']}
              disabled={entry.length >= v.digits}
              onClick={() => setEntry((e) => [...e, d])}
            >
              {d}
            </button>
          ))}
          <button
            type="button"
            className={styles['key']}
            aria-label={t('puzzle.safe.erase')}
            onClick={() => setEntry((e) => e.slice(0, -1))}
          >
            {'⌫'}
          </button>
        </div>
        <Button
          disabled={!full || jammed}
          onClick={() => {
            if (act(puzzleId, { kind: 'safe.try', digits: entry })) setEntry([]);
          }}
        >
          {jammed ? t('puzzle.safe.jammed') : t('puzzle.safe.try')}
        </Button>
        <ul className={styles['history']}>
          {(v.history ?? []).map((a, i) => (
            <li key={i}>
              <span className={styles['digits']}>{a.digits.join('')}</span>
              <span>{t('puzzle.safe.answer', { clicks: a.clicks, hums: a.hums })}</span>
            </li>
          ))}
        </ul>
        <HeardList puzzleId={puzzleId} ids={HEARD} />
      </div>
    );
  }

  const profile = v.clues.safeProfile;
  if (profile) {
    return (
      <div className={styles['stack']}>
        <p className={styles['muted']}>{t('puzzle.safe.profileHint')}</p>
        <ul className={styles['clues']}>
          {profile.map((line, i) => (
            <ClueLine key={i} phrase={constraintToPhrase(line)} />
          ))}
        </ul>
      </div>
    );
  }
  return <Bystander type={type} />;
}
